import { Bell, BellOff, Download, Loader2, AlertCircle } from 'lucide-react';
import { Button } from './ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from './ui/card';
import { Alert, AlertDescription } from './ui/alert';
import { usePushNotifications } from '../hooks/usePushNotifications';
import { useTranslation } from 'react-i18next';
import type { PushSource } from '../services/pushNotification';

interface PushNotificationToggleProps {
  showAsCard?: boolean;
  onStatusChange?: (enabled: boolean) => void;
  /** Which page it sits on, stored with the subscription it makes. */
  source?: PushSource;
}

/**
 * Push for the signed-in account on this device. Turning it off detaches this
 * account only: on a phone shared by several accounts the others keep theirs.
 */
export function PushNotificationToggle({
  showAsCard = false,
  onStatusChange,
  source = 'profile_toggle'
}: PushNotificationToggleProps) {
  const { t } = useTranslation();
  const {
    isSupported,
    isEnabled,
    isLoading,
    needsAppInstall,
    isIOSOrIPadOS,
    isMacOS,
    isAppInstalled,
    permission,
    enableNotifications,
    disableNotifications,
    error
  } = usePushNotifications();

  const handleToggle = async () => {
    try {
      if (isEnabled) {
        await disableNotifications();
        onStatusChange?.(false);
      } else {
        await enableNotifications(source);
        onStatusChange?.(true);
      }
    } catch (err) {
      // Error is already handled by the hook
      console.error('Toggle error:', err);
    }
  };

  const handleInstallPrompt = () => {
    // Show browser install prompt or guide
    alert(t('pushNotifications.installInstructions'));
  };

  if (!isSupported) {
    return null; // Don't show anything if not supported
  }

  const content = (
    <div className="space-y-4">
      {/* iOS/macOS Install Warning */}
      {(isIOSOrIPadOS || isMacOS) && !isAppInstalled && (
        <Alert variant="default" className="border-blue-500/50 bg-blue-500/10">
          <Download className="h-4 w-4 text-blue-500" />
          <AlertDescription className="text-sm">
            {isIOSOrIPadOS
              ? t('pushNotifications.iosInstallRequired')
              : t('pushNotifications.macosInstallRecommended')}
          </AlertDescription>
        </Alert>
      )}

      {/* Error Message */}
      {error && (
        <Alert variant="destructive">
          <AlertCircle className="h-4 w-4" />
          <AlertDescription className="text-sm">
            {error}
          </AlertDescription>
        </Alert>
      )}

      {/* Permission Denied Warning */}
      {permission === 'denied' && (
        <Alert variant="destructive">
          <AlertCircle className="h-4 w-4" />
          <AlertDescription className="text-sm">
            {t('pushNotifications.permissionDenied')}
          </AlertDescription>
        </Alert>
      )}

      {/* Status and Toggle */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          {isEnabled ? (
            <Bell className="h-5 w-5 text-green-500" />
          ) : (
            <BellOff className="h-5 w-5 text-muted-foreground" />
          )}
          <div>
            <p className="font-medium">
              {isEnabled
                ? t('pushNotifications.enabled')
                : t('pushNotifications.disabled')}
            </p>
            <p className="text-sm text-muted-foreground">
              {isEnabled
                ? t('pushNotifications.enabledDescription')
                : t('pushNotifications.disabledDescription')}
            </p>
          </div>
        </div>

        {needsAppInstall ? (
          <Button
            onClick={handleInstallPrompt}
            disabled={isLoading}
            variant="default"
          >
            <Download className="mr-2 h-4 w-4" />
            {t('pushNotifications.installApp')}
          </Button>
        ) : permission === 'denied' ? (
          <Button
            disabled
            variant="outline"
          >
            {t('pushNotifications.blocked')}
          </Button>
        ) : (
          <Button
            onClick={handleToggle}
            disabled={isLoading}
            variant={isEnabled ? 'outline' : 'default'}
          >
            {isLoading ? (
              <>
                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                {t('pushNotifications.loading')}
              </>
            ) : isEnabled ? (
              <>
                <BellOff className="mr-2 h-4 w-4" />
                {t('pushNotifications.disable')}
              </>
            ) : (
              <>
                <Bell className="mr-2 h-4 w-4" />
                {t('pushNotifications.enable')}
              </>
            )}
          </Button>
        )}
      </div>
    </div>
  );

  if (showAsCard) {
    return (
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Bell className="h-5 w-5" />
            {t('pushNotifications.title')}
          </CardTitle>
          <CardDescription>
            {t('pushNotifications.cardDescription')}
          </CardDescription>
        </CardHeader>
        <CardContent>
          {content}
        </CardContent>
      </Card>
    );
  }

  return content;
}
